import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Search,
  RefreshCcw,
  Truck,
  Store,
  Package,
  CheckCircle,
  Clock,
  XCircle,
  ChefHat,
  ShoppingBag,
  ArrowRight,
  Filter,
  AlertCircle,
  Printer, 
  Paperclip,
  MapPin, 
  MessageCircle, 
  Phone,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { toast } from "react-hot-toast";
import { supabase } from "../supabase";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import {
  buildStoreSiteContext,
  extractRequestedOrderDate,
  fetchStoreSiteContext,
  formatRequestedOrderDate,
  getStoreOrderOrigin,
  isStoreOrderCanceledStatus,
  isStoreOrderClosedStatus,
  isStoreOrderDeliveredStatus,
  isStoreOrderFromWeb,
  resolveStoreOrderSiteId,
  reverseStoreOrderStock,
} from "../utils/storeLogistics";
import {
  reconcileStoreOrdersClientAccounts,
  syncStoreOrderClientAccount,
} from "../utils/storeOrderAccounting";
import { getSaleItemDisplayName } from "../utils/productLabels";
import { buildAfipQrUrl, buildFiscalTicketHtml, resolveTicketEmitter } from "../utils/ticketFiscal";

// Librerías para PDF
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';

// Sonido de notificación
const NOTIFICATION_SOUND = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';

// Títulos de comprobantes para el PDF
const CBTE_TITULOS = {
  1: 'FACTURA A', 6: 'FACTURA B', 11: 'FACTURA C',
  2: 'NOTA DE DÉBITO A', 7: 'NOTA DE DÉBITO B', 12: 'NOTA DE DÉBITO C',
  3: 'NOTA DE CRÉDITO A', 8: 'NOTA DE CRÉDITO B', 13: 'NOTA DE CRÉDITO C'
};

// Imprime un HTML en un iframe oculto (mismo criterio que el POS: sin popup,
// que el navegador bloquea después de un await).
const printTicketHtml = (html) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally {
      setTimeout(() => iframe.remove(), 60000);
    }
  }, 400);
};

/**
 * PedidosWeb.jsx - Diseño Estandarizado (Modelo Productos)
 * - Encabezados oscuros en tablas.
 * - Inputs y filtros con estilo redondeado y gris suave.
 * - Tipografía uppercase y tracking-widest en headers.
 * - Funcionalidad intacta (Impresión Robusta + WhatsApp).
 * * ACTUALIZACIÓN: Lógica de notificación configurable.
 */
export default function PedidosWeb({ onNavigate }) {
  const { profile, organization } = useAuth();
  // ✅ FIX bug #11 multi-sucursal: filtramos los pedidos web según la sucursal de
  // despacho activa. Antes la página era 100% global y cada cajero veía pedidos de
  // todas las sucursales — confuso en multi-local. Si está "Consolidado" sigue
  // mostrando todos.
  const { selectedSiteId, selectedSite } = useSite();
  const navigate = useNavigate();
  const location = useLocation();
  const audioRef = useRef(new Audio(NOTIFICATION_SOUND));
  const refreshTimeoutRef = useRef(null);
  const isFetchingOrdersRef = useRef(false);
  const pendingRefreshRef = useRef(false);
  const reconciledOnceRef = useRef(false);
  
  // --- ESTADOS ---
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [storeType, setStoreType] = useState('retail');
  const [filterStatus, setFilterStatus] = useState('active'); 
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [cancelConfirm, setCancelConfirm] = useState({ open: false, order: null, nextStatus: '' });
  
  // Configuración de notificaciones
  const [notifConfig, setNotifConfig] = useState({ sound: true, push: false, toast: true });
  const [storeSiteContext, setStoreSiteContext] = useState(() => buildStoreSiteContext());
  
  // Datos de la empresa para encabezados de PDF
  const [empresa, setEmpresa] = useState(null);

  // --- CARGA INICIAL ---
  useEffect(() => {
    if (profile?.organization_id) {
      fetchConfig();
      fetchOrders();
      
      // SUSCRIPCIÓN REALTIME
      const channel = supabase
        .channel('public:store_orders')
        .on(
          'postgres_changes',
          {
            event: '*', 
            schema: 'public',
            table: 'store_orders',
            filter: `organization_id=eq.${profile.organization_id}`
          },
          (payload) => {
             if (payload.eventType === 'INSERT' && isStoreOrderFromWeb(payload.new)) {
                // Ejecutar notificaciones según configuración
                handleNotification(payload.new);
                scheduleRefresh(200); 
             } else if (payload.eventType === 'UPDATE' && (isStoreOrderFromWeb(payload.new) || isStoreOrderFromWeb(payload.old))) {
                scheduleRefresh(250); 
             }
          }
        )
        .subscribe();

      return () => {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
          refreshTimeoutRef.current = null;
        }
        supabase.removeChannel(channel);
      };
    }
    // eslint-disable-next-line
  }, [profile?.organization_id]);

  useEffect(() => {
    const dashboardFocus = location.state?.dashboardFocus;
    if (dashboardFocus?.type === 'pedidos') {
      setFilterStatus('new');
    }
  }, [location.state]);

  const handleNotification = (newOrder) => {
      // 1. Sonido
      if (notifConfig.sound) {
          try {
              audioRef.current.currentTime = 0;
              audioRef.current.play().catch(e => console.log("Audio play blocked", e));
          } catch (e) {
              console.error("Error audio", e);
          }
      }

      // 2. Toast en Pantalla
      if (notifConfig.toast) {
          toast.success(`¡Nuevo pedido! #${newOrder.tracking_code}`, { duration: 5000, icon: '🔔' });
      }

      // 3. Notificación Push (Navegador)
      if (notifConfig.push && "Notification" in window && Notification.permission === "granted") {
          try {
              new Notification("Nuevo Pedido Recibido", {
                  body: `Pedido #${newOrder.tracking_code} acaba de ingresar.`,
                  icon: '/icon-192x192.png' // Asegúrate de tener un icono o usa uno genérico
              });
          } catch (e) {
              console.error("Error push notification", e);
          }
      }
  };

  const fetchConfig = async () => {
    try {
      // org viene del AuthContext (no hace falta refetch por página)
      const [storeRes, storeSiteContextRes] = await Promise.all([
          supabase.from('store_config').select('store_type, notification_config, default_fulfillment_site_id').eq('organization_id', profile.organization_id).maybeSingle(),
          fetchStoreSiteContext({ supabase, organizationId: profile.organization_id }),
      ]);

      if (storeRes.data?.store_type) setStoreType(storeRes.data.store_type);
      if (storeRes.data?.notification_config) {
          setNotifConfig(prev => ({ ...prev, ...storeRes.data.notification_config }));
      }
      if (organization) setEmpresa(organization);
      if (!storeSiteContextRes.error) setStoreSiteContext(storeSiteContextRes.data);

    } catch (e) {
      console.error("Error config:", e);
    }
  };

  const fetchOrders = useCallback(async ({ showLoader = true } = {}) => {
    if (!profile?.organization_id) return;

    if (isFetchingOrdersRef.current) {
      pendingRefreshRef.current = true;
      return;
    }

    isFetchingOrdersRef.current = true;
    if (showLoader) setLoading(true);

    try {
      const { data, error } = await supabase
        .from('store_orders')
        .select(`
          *,
          sales:sales!store_orders_sale_id_fkey (
             total,
             site_id,
             payment_method,
             invoice_status,
             invoice_cbte_tipo,
             numero_factura,
             cae,
             cae_vencimiento
          )
        `)
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      // ✅ FIX bug #11: filtramos por sucursal de despacho activa cuando el usuario
      // tiene una sucursal elegida. Usamos resolveStoreOrderSiteId para resolver el
      // sitio del pedido (sale.site_id, logistics_site_id, default por slug, etc.).
      const allWebOrders = (data || []).filter(isStoreOrderFromWeb);
      const webOrders = selectedSiteId
        ? allWebOrders.filter((order) => resolveStoreOrderSiteId(order, storeSiteContext) === selectedSiteId)
        : allWebOrders;
      setOrders(webOrders);

      if (!reconciledOnceRef.current) {
        reconciledOnceRef.current = true;
        Promise.resolve()
          .then(() =>
            reconcileStoreOrdersClientAccounts({
              orders: webOrders,
              organizationId: profile.organization_id,
            }),
          )
          .catch((reconcileError) => {
            console.error("Error reconciling store orders:", reconcileError);
          });
      }

    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Error de conexión al cargar pedidos.");
    } finally {
      isFetchingOrdersRef.current = false;
      setLoading(false);

      if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        fetchOrders({ showLoader: false });
      }
    }
  }, [profile?.organization_id, selectedSiteId, storeSiteContext]);

  // ✅ FIX bug #11: re-aplicamos el filtro cuando cambia la sucursal en el Topbar.
  useEffect(() => {
    if (!profile?.organization_id) return;
    fetchOrders({ showLoader: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteId]);

  const scheduleRefresh = useCallback((delay = 350) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    refreshTimeoutRef.current = window.setTimeout(() => {
      refreshTimeoutRef.current = null;
      fetchOrders({ showLoader: false });
    }, delay);
  }, [fetchOrders]);

  // --- LÓGICA DE IMPRESIÓN INTELIGENTE (Ticket vs Factura) ---
  const handlePrint = async (e, order) => {
      e.stopPropagation(); 
      const toastId = toast.loading("Generando documento...");
      
      try {
          let saleData = null;
          let saleItems = [];
          let clientData = null;

          if (order.sale_id) {
            const { data: sale, error } = await supabase
                .from('sales')
                .select(`
                    *,
                    sale_items (
                        quantity, unit_price, subtotal, iva_percentage, combo_role, product_name_snapshot,
                        products (barcode, iva_percentage)
                    ),
                    clients (
                        full_name, doc_type, doc_number, tax_condition, address, city, phone
                    )
                `)
                .eq('id', order.sale_id)
                .maybeSingle(); 

            if (!error && sale) {
                saleData = sale;
                saleItems = sale.sale_items;
                clientData = sale.clients;
            } else {
                console.warn("No se pudo obtener la venta vinculada o error de API:", error);
            }
          }

          const snapshot = order.customer_snapshot || {};
          const shipAddr = order.shipping_address || {};

          const finalClientName = clientData?.full_name || snapshot.name || 'Cliente Web';
          const finalClientDoc = clientData?.doc_number || snapshot.doc_number || '-';
          const finalClientAddress = clientData?.address || shipAddr.address || '-';
          const finalClientPhone = clientData?.phone || snapshot.phone || order.customer_phone || '-';

          const isFacturado = saleData?.invoice_status === 'facturado';
          const printConfig = empresa?.print_config || {};

          // Factura con CAE: mismo comprobante que POS / Facturación / Historial
          // (src/utils/ticketFiscal.js). Antes salía un PDF con el QR armado con
          // ptoVta fijo en 1 y el CUIT de la organización: apuntaba a otro
          // comprobante. La orden de pedido (sin CAE) sigue siendo el PDF de abajo.
          if (isFacturado && saleData?.cae) {
              let billingEntity = null;
              if (saleData.billing_entity_id) {
                  const { data: be } = await supabase
                      .from('billing_entities')
                      .select('id, tax_name, cuit, tax_condition, fiscal_configuration, afip_pto_venta, iibb, inicio_actividades, domicilio_comercial')
                      .eq('id', saleData.billing_entity_id)
                      .maybeSingle();
                  billingEntity = be || null;
              }
              const qrUrl = buildAfipQrUrl({
                  sale: saleData,
                  emitterCuit: billingEntity?.cuit || empresa?.cuit,
                  client: clientData,
                  ptoVentaFallback: billingEntity?.afip_pto_venta,
              });
              const qrDataUrl = qrUrl
                  ? await QRCode.toDataURL(qrUrl, { margin: 1, width: 300, errorCorrectionLevel: 'M' })
                  : null;
              const html = buildFiscalTicketHtml({
                  sale: saleData,
                  items: saleItems || [],
                  client: clientData,
                  emitter: resolveTicketEmitter({ organization: empresa, billingEntity }),
                  config: printConfig.factura || { printerWidth: '80mm', showClientInfo: true },
                  qrDataUrl,
              });
              printTicketHtml(html);
              toast.success("Comprobante listo para imprimir", { id: toastId });
              return;
          }
          
          const docType = isFacturado ? 'factura' : 'ticket_venta';
          const docConfig = printConfig[docType] || {
              showLogo: true,
              headerText: empresa?.name || 'Ticket de Venta',
              printerWidth: '80mm',
              showClientInfo: true
          };

          const doc = new jsPDF({
              unit: 'mm',
              format: docConfig.printerWidth === 'a4' ? 'a4' : [parseInt(docConfig.printerWidth?.replace('mm','') || 80), 297]
          });
          
          const W = doc.internal.pageSize.getWidth();
          let y = 10;

          if (docConfig.showLogo && docConfig.logoBase64) {
              try {
                  const logoH = 15;
                  doc.addImage(docConfig.logoBase64, 'PNG', 10, y, 30, logoH);
                  y += logoH + 5;
              } catch(e) { /* recurso opcional del PDF */ }
          }

          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          
          const tituloDoc = isFacturado 
            ? (CBTE_TITULOS[saleData.invoice_cbte_tipo] || 'FACTURA') 
            : 'ORDEN DE PEDIDO'; 
            
          doc.text(tituloDoc, W/2, y, { align: 'center' });
          y += 5;
          
          if (isFacturado && saleData.numero_factura) {
              doc.setFontSize(9);
              doc.setFont('helvetica', 'normal');
              doc.text(`N°: ${saleData.numero_factura}`, W/2, y, { align: 'center' });
              y += 5;
          }

          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text(`ID: #${order.tracking_code}`, W/2, y, { align: 'center' });
          y += 4;
          doc.text(new Date(order.created_at).toLocaleString('es-AR'), W/2, y, { align: 'center' });
          y += 8;

          if (docConfig.showClientInfo) {
              doc.setFont('helvetica', 'bold');
              doc.text("CLIENTE:", 5, y);
              y += 4;
              doc.setFont('helvetica', 'normal');
              doc.text(`${finalClientName} (${finalClientDoc})`, 5, y);
              y += 4;
              
              if (finalClientAddress !== '-') {
                  doc.text(`Dir: ${finalClientAddress}`, 5, y);
                  y += 4;
              }
              if (finalClientPhone !== '-') {
                  doc.text(`Tel: ${finalClientPhone}`, 5, y);
                  y += 4;
              }
              y += 2;
          }

          let rows = [];
          if (saleItems.length > 0) {
              rows = saleItems.map(item => [
                  `${item.quantity}x ${getSaleItemDisplayName(item).substring(0,20)}`,
                  `$${Number(item.unit_price * item.quantity).toLocaleString('es-AR')}`
              ]);
          } else {
              rows = [['Total del Pedido', `$${Number(saleData?.total || 0).toLocaleString('es-AR')}`]];
          }

          autoTable(doc, {
              startY: y,
              head: [['Cant/Prod', 'Total']],
              body: rows,
              theme: 'plain',
              styles: { fontSize: 8, cellPadding: 1 },
              columnStyles: { 1: { halign: 'right' } },
              margin: { left: 2, right: 2 }
          });

          y = doc.lastAutoTable.finalY + 5;

          const totalFinal = saleData?.total || 0;

          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(`TOTAL: $ ${Number(totalFinal).toLocaleString('es-AR')}`, W - 5, y, { align: 'right' });
          y += 6;

          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text(`Pago: ${saleData?.payment_method || 'PENDIENTE'}`, 5, y);
          
          if (isFacturado && saleData.cae) {
              y += 5;
              try {
                  const qrData = JSON.stringify({
                      ver: 1,
                      fecha: saleData.created_at.slice(0,10),
                      cuit: parseInt(empresa?.cuit?.replace(/\D/g,'')) || 0,
                      ptoVta: 1, 
                      tipoCmp: parseInt(saleData.invoice_cbte_tipo) || 11,
                      nroCmp: parseInt(saleData.numero_factura?.split('-')[1]) || 0,
                      importe: saleData.total,
                      moneda: 'PES',
                      ctz: 1,
                      codAut: parseInt(saleData.cae) || 0
                  });
                  const qrUrl = await QRCode.toDataURL(`https://www.afip.gob.ar/fe/qr/?p=${btoa(qrData)}`);
                  doc.addImage(qrUrl, 'PNG', 5, y, 20, 20);
                  doc.text(`CAE: ${saleData.cae}`, 30, y + 10);
                  doc.text(`Vto: ${saleData.cae_vencimiento?.slice(0,10) || '-'}`, 30, y + 15);
              } catch(e) { /* recurso opcional del PDF */ }
          }

          doc.save(`${isFacturado ? 'Factura' : 'Pedido'}_${order.tracking_code}.pdf`);
          toast.success("Descargado", { id: toastId });

      } catch (err) {
          console.error(err);
          if (err.message?.includes("Token") || err.code === "401" || err.code === "400") {
             toast.error("Sesión expirada. Recarga la página.", { id: toastId });
          } else {
             toast.error("Error al generar PDF.", { id: toastId });
          }
      }
  };

  const handleStatusChange = async (order, newStatus) => {
      if (order.status === newStatus) return;
      const toastId = toast.loading("Actualizando...");

      try {
          const { error: orderError } = await supabase
              .from('store_orders')
              .update({ status: newStatus, organization_id: profile.organization_id })
              .eq('id', order.id);

          if (orderError) throw orderError;

          const saleStatus =
            isStoreOrderDeliveredStatus(newStatus)
              ? 'completed'
              : newStatus === 'cancelado'
                ? 'canceled'
                : 'pending';

          if (order.sale_id) {
              const { error: saleError } = await supabase
                  .from('sales')
                  .update({ status: saleStatus })
                  .eq('id', order.sale_id);
              
              if (saleError) console.error("Error sync sales:", saleError);
          }

          const nextStatusNormalized = String(newStatus || '').toLowerCase();

          if (order.sale_id) {
            const clientAccountSync = await syncStoreOrderClientAccount({
              orderId: order.id,
              saleId: order.sale_id,
              organizationId: profile.organization_id,
              trackingCode: order.tracking_code,
              nextStatus: newStatus,
              shippingCost: order.shipping_cost || 0,
              orderOrigin: getStoreOrderOrigin(order),
            });

            if (nextStatusNormalized !== 'cancelado' && clientAccountSync?.reason === 'missing_client') {
              toast('Pedido entregado, pero no tiene cliente vinculado para cuenta corriente.', {
                icon: '!',
              });
            }
          }

          // REVERSIÓN DE STOCK SI SE CANCELA EL PEDIDO
          if (isStoreOrderCanceledStatus(newStatus) && order.sale_id) {
              try {
                  const reverseRes = await reverseStoreOrderStock({
                      supabase,
                      organizationId: profile?.organization_id,
                      order,
                      siteContext: storeSiteContext,
                  });

                  if (reverseRes.error) throw reverseRes.error;
              } catch (stockError) {
                  console.error("Error revirtiendo stock:", stockError);
                  toast.error("Pedido cancelado, pero no se pudo revertir el stock.");
              }
          }

          toast.success("Estado actualizado", { id: toastId });
          setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: newStatus } : o));

      } catch (err) {
          console.error(err);
          toast.error("No se pudo actualizar", { id: toastId });
      }
  };

  const requestStatusChange = (order, nextStatus) => {
    if (order.status === nextStatus) return;

    // Un pedido cancelado es TERMINAL: salir de 'cancelado' no vuelve a descontar
    // stock, y volver a cancelarlo lo repondría otra vez (stock inflado). Se
    // bloquea la transición de salida para evitar la doble reversión de stock.
    if (isStoreOrderCanceledStatus(order.status)) {
      toast.error('Un pedido cancelado no puede cambiar de estado.');
      return;
    }

    if (nextStatus === 'cancelado') {
      setCancelConfirm({ open: true, order, nextStatus });
      return;
    }

    handleStatusChange(order, nextStatus);
  };

  const closeCancelConfirm = () => {
    setCancelConfirm({ open: false, order: null, nextStatus: '' });
  };

  const confirmCancelOrder = async () => {
    if (!cancelConfirm.order || !cancelConfirm.nextStatus) {
      closeCancelConfirm();
      return;
    }

    const targetOrder = cancelConfirm.order;
    const targetStatus = cancelConfirm.nextStatus;
    closeCancelConfirm();
    await handleStatusChange(targetOrder, targetStatus);
  };

  const getStatusMeta = (status, deliveryType) => {
    const s = isStoreOrderDeliveredStatus(status) ? 'entregado' : String(status || '').toLowerCase();
    const map = {
        recibido: { label: 'Nuevo', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: AlertCircle },
        en_preparacion: { label: storeType === 'gastronomy' ? 'Cocinando' : 'Preparando', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Package },
        listo: { label: deliveryType === 'retiro' ? 'Listo p/Retirar' : 'Listo', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: CheckCircle },
        en_camino: { label: 'En Camino', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Truck },
        entregado: { label: deliveryType === 'retiro' ? 'Retirado' : 'Entregado', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
        cancelado: { label: 'Cancelado', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
    };
    return map[s] || { label: s, color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock };
  };

  const formatCurrency = (amount) => 
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(amount || 0);

  const formatDate = (dateStr) => {
    if(!dateStr) return '-';
    const date = new Date(dateStr);
    const isToday = new Date().toDateString() === date.toDateString();
    return isToday 
        ? date.toLocaleString('es-AR', { hour: '2-digit', minute: '2-digit' }) + ' hs'
        : date.toLocaleString('es-AR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatForWhatsapp = (phone) => {
      if (!phone) return null;
      const clean = phone.replace(/\D/g, '');
      if (clean.length < 8) return null; 
      return clean.startsWith('54') ? clean : `549${clean}`;
  };

  const kpis = useMemo(() => {
    const active = orders.filter((order) => !isStoreOrderClosedStatus(order.status));
    const nuevos = orders.filter(o => o.status === 'recibido');
    const prep = orders.filter(o => ['en_preparacion', 'listo'].includes(o.status));
    
    const today = new Date().toISOString().split('T')[0];
    const salesToday = orders
        .filter(o => o.created_at?.startsWith(today) && o.status !== 'cancelado')
        .reduce((acc, curr) => acc + (curr.sales?.total || 0), 0);

    return { activeCount: active.length, newCount: nuevos.length, prepCount: prep.length, salesToday };
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
        const searchLower = searchTerm.toLowerCase();
        const matchText = 
            order.tracking_code?.toLowerCase().includes(searchLower) ||
            order.customer_snapshot?.name?.toLowerCase().includes(searchLower);
        
        if (!matchText) return false;

        if (filterStatus === 'all') return true;
        if (filterStatus === 'active') return !isStoreOrderClosedStatus(order.status);
        if (filterStatus === 'completed') return isStoreOrderDeliveredStatus(order.status) || isStoreOrderCanceledStatus(order.status);
        if (filterStatus === 'new') return order.status === 'recibido';
        
        return order.status === filterStatus;
    });
  }, [orders, searchTerm, filterStatus]);

  const getSortableValue = (order, key) => {
    switch (key) {
      case 'created_at':
        return new Date(order?.created_at || 0).getTime();
      case 'customer':
        return String(order?.customer_snapshot?.name || 'Cliente Web').toLowerCase();
      case 'delivery_type':
        return String(order?.delivery_type || '').toLowerCase();
      case 'total':
        return Number(order?.sales?.total || 0);
      case 'status':
        return String(order?.status || '').toLowerCase();
      default:
        return '';
    }
  };

  const sortedOrders = useMemo(() => {
    const items = [...filteredOrders];
    const { key, direction } = sortConfig;
    const factor = direction === 'asc' ? 1 : -1;

    items.sort((a, b) => {
      const aValue = getSortableValue(a, key);
      const bValue = getSortableValue(b, key);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * factor;
      }

      return String(aValue).localeCompare(String(bValue), 'es-AR', { numeric: true, sensitivity: 'base' }) * factor;
    });

    return items;
  }, [filteredOrders, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'created_at' || key === 'total' ? 'desc' : 'asc' }
    ));
  };

  const FilterHint = ({ text }) => (
    <span
      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-200 bg-white text-[9px] font-black text-slate-400 cursor-help"
      title={text}
    >
      ?
    </span>
  );

  const SortableTh = ({ label, sortKey, align = 'left', title }) => {
    const isActive = sortConfig.key === sortKey;
    const Icon = isActive && sortConfig.direction === 'asc' ? ChevronUp : ChevronDown;
    const alignClass = align === 'center' ? 'justify-center text-center' : align === 'right' ? 'justify-end text-right' : 'justify-start text-left';

    return (
      <th className={`px-3.5 py-3 ${align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left'}`}>
        <button
          type="button"
          onClick={() => handleSort(sortKey)}
          title={title || `Ordenar por ${label}`}
          className={`inline-flex w-full items-center gap-1.5 font-black uppercase tracking-widest transition-colors ${alignClass} ${isActive ? 'text-slate-700' : 'text-slate-400 hover:text-slate-600'}`}
        >
          <span>{label}</span>
          <Icon size={12} className={isActive ? 'text-[#0B2A36]' : 'text-slate-300'} />
        </button>
      </th>
    );
  };

  const handleRowClick = (e, order) => {
    if (
        e.target.tagName === 'SELECT' || 
        e.target.closest('select') || 
        e.target.closest('button') || 
        e.target.closest('a')
    ) return;
    
    if (onNavigate) {
      onNavigate('pedido-web-detalle', order.id);
    } else {
      navigate(`/sistema/pedidos-web/${order.id}`);
    }
  };

  return (
    // ESTILO GENERAL DE PÁGINA (Igual a Productos)
    <div className="imp-page imp-container animate-nova-in px-3 py-3 sm:px-4 sm:py-4 lg:px-5">
      
      {/* 1. HEADER (Diseño Productos) */}
      <div className="imp-header mb-4 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
             {storeType === 'gastronomy' ? <ChefHat size={18} className="text-[#3FF2AA]"/> : <Store size={18} className="text-[#3FF2AA]"/>}
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Pedidos Web</h1>
            <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Canal Digital — Gestión de entregas
            </p>
          </div>
        </div>

        <div className="imp-header-actions w-full md:w-auto">
             <button 
                onClick={() => fetchOrders()} 
                title="Vuelve a consultar los pedidos web y actualiza los indicadores."
                className="w-full md:w-auto px-4 py-2.5 rounded-2xl bg-[#51A7FD] text-white shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
             >
                 <RefreshCcw size={16} className={loading ? "animate-spin" : ""} />
                 Actualizar
             </button>
             <button 
                onClick={() => onNavigate ? onNavigate('tienda-config') : navigate('/sistema/tienda-config')}
                title="Abre la configuración de la tienda online y sus reglas operativas."
                className="w-full md:w-auto px-4 py-2.5 rounded-2xl bg-[#0B2A36] text-white shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
             >
                 <Store size={16} className="text-[#3FF2AA]" />
                 Configurar
             </button>
        </div>
      </div>

      {/* 2. KPIs (Diseño Productos) */}
      <div className="imp-kpis mb-3">
          <div className="imp-kpi">
             <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pendientes</p>
                <AlertCircle size={16} className="text-slate-300" />
             </div>
             <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.activeCount}</p>
             <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Pedidos en curso</p>
          </div>

          <div className={`imp-kpi transition-all ${kpis.newCount > 0 ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-100' : ''}`}>
             <div className="flex items-center justify-between">
                <p className={`text-[9px] font-black uppercase tracking-widest ${kpis.newCount > 0 ? 'text-blue-700' : 'text-slate-400'}`}>Nuevos</p>
                <AlertCircle size={16} className={kpis.newCount > 0 ? 'text-blue-500 animate-pulse' : 'text-slate-300'} />
             </div>
             <p className={`mt-1.5 text-2xl font-black tracking-tight ${kpis.newCount > 0 ? 'text-blue-800' : 'text-slate-800'}`}>{kpis.newCount}</p>
             <p className={`text-[10px] font-bold uppercase tracking-widest ${kpis.newCount > 0 ? 'text-blue-600' : 'text-slate-400'}`}>Requieren atención</p>
          </div>

          <div className="imp-kpi">
             <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Preparación</p>
                <Package size={16} className="text-slate-300" />
             </div>
             <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.prepCount}</p>
             <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">En cocina / Armando</p>
          </div>

          <div className="imp-kpi">
             <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ventas Hoy</p>
                <ShoppingBag size={16} className="text-slate-300" />
             </div>
             <p className="mt-1.5 text-2xl font-black text-emerald-600 tracking-tight">{formatCurrency(kpis.salesToday)}</p>
             <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Ingresos brutos web</p>
          </div>
      </div>

      {/* 3. BARRA DE HERRAMIENTAS (Diseño Productos) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-2.5 items-center">
              {/* BUSCADOR */}
              <div className="sm:col-span-2 xl:col-span-5 relative">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
                  <Search size={12} /> Buscar <FilterHint text="Filtra por código de pedido o nombre del cliente." />
                </label>
                <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                    <input 
                        type="text"
                        placeholder="CÓDIGO, CLIENTE..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
                    />
                </div>
              </div>

              {/* FILTROS (BOTONES) */}
              <div className="xl:col-span-7 flex flex-wrap items-end gap-2 justify-start xl:justify-end">
                 <div className="flex bg-slate-50 p-1 rounded-2xl border border-slate-100">
                     {[
                        { id: 'active', label: 'Activos' },
                        { id: 'new', label: 'Nuevos' },
                        { id: 'completed', label: 'Historial' },
                        { id: 'all', label: 'Todos' },
                     ].map(tab => (
                         <button
                            key={tab.id}
                            onClick={() => setFilterStatus(tab.id)}
                            title={
                                tab.id === 'active' ? 'Muestra pedidos todavía en curso.' :
                                tab.id === 'new' ? 'Muestra solo pedidos recién recibidos.' :
                                tab.id === 'completed' ? 'Muestra pedidos entregados o cancelados.' :
                                'Muestra todos los pedidos sin filtrar por estado.'
                            }
                            className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                                filterStatus === tab.id 
                                    ? 'bg-[#0B2A36] text-white shadow-md' 
                                    : 'text-slate-400 hover:text-slate-600 hover:bg-white'
                            }`}
                         >
                            {tab.label}
                         </button>
                     ))}
                 </div>
              </div>
          </div>
      </div>

      {/* 4. TABLA (Diseño Productos - Encabezado Oscuro) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex-1 flex flex-col">
          
          <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck size={16} className="text-[#3FF2AA]" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest">Listado de Pedidos ({sortedOrders.length})</span>
            </div>
          </div>

          <div className="lg:hidden p-3 space-y-2.5 bg-slate-50/40">
              {loading ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center">
                      <RefreshCcw className="animate-spin mb-2 text-slate-300 mx-auto" size={24}/>
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">Cargando...</span>
                  </div>
              ) : sortedOrders.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center text-slate-300 text-[10px] font-black uppercase tracking-[0.25em]">
                      Sin resultados
                  </div>
              ) : (
                  sortedOrders.map((order) => {
                      const status = getStatusMeta(order.status, order.delivery_type);
                      const isNew = order.status === 'recibido';
                      const isFacturado = order.sales?.invoice_status === 'facturado';
                      const proofUrl = order.shipping_address?.proof_url;
                      const customerName = order.customer_snapshot?.name || 'Cliente Web';
                      const customerPhone = order.customer_phone || order.customer_snapshot?.phone;
                      const customerAddress = order.shipping_address?.address || order.customer_snapshot?.address;
                      const wsNumber = formatForWhatsapp(customerPhone);
                      const requestedDate = extractRequestedOrderDate(order);
                      const requestedDateLabel = formatRequestedOrderDate(requestedDate);

                      return (
                          <div
                            key={order.id}
                            onClick={(e) => handleRowClick(e, order)}
                            className={`rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-all ${isNew ? 'ring-1 ring-blue-100 bg-blue-50/20' : ''}`}
                          >
                              <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                      <div className="font-black text-slate-800 text-[11px] uppercase flex items-center gap-2">
                                          #{order.tracking_code}
                                          {isNew && <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>}
                                      </div>
                                      <div className="text-[9px] font-bold text-slate-400 mt-1 flex items-center gap-1 uppercase tracking-wider">
                                          <Clock size={10}/> {formatDate(order.created_at)}
                                      </div>
                                      {requestedDate && (
                                          <div className="text-[9px] font-bold text-sky-600 mt-1 uppercase tracking-wider">
                                              Para: {requestedDateLabel}
                                          </div>
                                      )}
                                  </div>
                                  <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-xl border text-[9px] font-black uppercase tracking-widest ${status.color}`}>
                                      <status.icon size={12} />
                                      {status.label}
                                  </div>
                              </div>

                              <div className="mt-3 space-y-2">
                                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                                      <div className="font-bold text-slate-700 text-[10px] uppercase truncate">{customerName}</div>
                                      {customerAddress && (
                                          <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-1 flex items-center gap-1 truncate" title={customerAddress}>
                                              <MapPin size={9} /> {customerAddress}
                                          </div>
                                      )}
                                      {customerPhone ? (
                                          <div className="flex items-center gap-2 mt-1">
                                              <span className="text-[9px] font-bold text-slate-500 flex items-center gap-1">
                                                  <Phone size={9} /> {customerPhone}
                                              </span>
                                              {wsNumber && (
                                                  <a
                                                    href={`https://wa.me/${wsNumber}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="bg-emerald-100 text-emerald-600 p-1 rounded hover:bg-emerald-200 transition-colors"
                                                    title="Abrir WhatsApp"
                                                  >
                                                      <MessageCircle size={10} />
                                                  </a>
                                              )}
                                          </div>
                                      ) : (
                                          <div className="text-[9px] font-bold text-slate-300 uppercase tracking-wider mt-1">Sin teléfono</div>
                                      )}
                                  </div>

                                  <div className="grid grid-cols-2 gap-2">
                                      <div className="rounded-xl bg-slate-50 px-3 py-2">
                                          <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Entrega</div>
                                          <div className="mt-1 flex items-center gap-2 text-[9px] font-black text-slate-600 uppercase tracking-widest">
                                              <span className={`p-1.5 rounded-lg border ${order.delivery_type === 'envio' ? 'bg-purple-50 border-purple-100 text-purple-600' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                                                  {order.delivery_type === 'envio' ? <Truck size={12}/> : <Store size={12}/>}
                                              </span>
                                              {order.delivery_type}
                                          </div>
                                      </div>
                                      <div className="rounded-xl bg-slate-50 px-3 py-2 text-right">
                                          <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Total</div>
                                          <div className="mt-1 font-black text-slate-900 text-[11px]">{formatCurrency(order.sales?.total)}</div>
                                          <div className="text-[8px] font-bold text-slate-400 uppercase mt-1 tracking-widest">
                                              {order.sales?.payment_method || 'PENDIENTE'}
                                          </div>
                                      </div>
                                  </div>
                              </div>

                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                  <select
                                    value={order.status}
                                    onChange={(e) => requestStatusChange(order, e.target.value)}
                                    title="Cambia el estado operativo de este pedido."
                                    className={`flex-1 min-w-[170px] outline-none text-[9px] font-black uppercase tracking-widest py-2 px-3 rounded-xl border cursor-pointer appearance-none text-center transition-all ${status.color}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                      <option value="recibido">Nuevo</option>
                                      <option value="en_preparacion">Preparando</option>
                                      {order.delivery_type === 'retiro' ? (
                                          <>
                                              <option value="listo">Listo p/ Retirar</option>
                                              <option value="entregado">Retirado</option>
                                          </>
                                      ) : (
                                          <>
                                              <option value="listo">Listo</option>
                                              <option value="en_camino">En Camino</option>
                                              <option value="entregado">Entregado</option>
                                          </>
                                      )}
                                      <option value="cancelado">Cancelado</option>
                                  </select>

                                  {proofUrl && (
                                      <a
                                          href={proofUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="p-2 bg-white border border-slate-200 text-emerald-600 rounded-xl hover:text-emerald-700 hover:border-emerald-200 transition-all shadow-sm"
                                          title="Ver Comprobante de Pago"
                                      >
                                          <Paperclip size={16} />
                                      </a>
                                  )}

                                  <button
                                    onClick={(e) => handlePrint(e, order)}
                                    className="p-2 bg-white border border-slate-200 text-slate-500 rounded-xl hover:text-blue-600 hover:border-blue-200 transition-all shadow-sm"
                                    title={isFacturado ? "Imprimir Factura" : "Imprimir Ticket Interno"}
                                  >
                                      <Printer size={16} />
                                  </button>
                              </div>
                          </div>
                      );
                  })
              )}
          </div>

          <div className="hidden lg:block overflow-x-auto custom-scrollbar">
              <table className="w-full min-w-[980px]">
                  <thead className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                      <tr>
                          <SortableTh label="Pedido" sortKey="created_at" title="Ordenar por fecha y hora del pedido" />
                          <SortableTh label="Cliente / Datos" sortKey="customer" title="Ordenar por cliente" />
                          <SortableTh label="Entrega" sortKey="delivery_type" title="Ordenar por tipo de entrega" />
                          <SortableTh label="Total" sortKey="total" title="Ordenar por total" />
                          <SortableTh label="Estado" sortKey="status" align="center" title="Ordenar por estado" />
                          <th className="px-3.5 py-3 text-right">Acciones</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                      {loading ? (
                          <tr>
                              <td colSpan="6" className="px-6 py-12 text-center">
                                  <div className="flex flex-col items-center justify-center opacity-50">
                                      <RefreshCcw className="animate-spin mb-2 text-slate-300" size={24}/> 
                                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">Cargando...</span>
                                  </div>
                              </td>
                          </tr>
                      ) : sortedOrders.length === 0 ? (
                          <tr>
                              <td colSpan="6" className="px-6 py-12 text-center text-slate-300 text-[10px] font-black uppercase tracking-[0.25em]">
                                  Sin resultados
                              </td>
                          </tr>
                      ) : (
                          sortedOrders.map((order) => {
                              const status = getStatusMeta(order.status, order.delivery_type);
                              const isNew = order.status === 'recibido';
                              const isFacturado = order.sales?.invoice_status === 'facturado';
                              const proofUrl = order.shipping_address?.proof_url; 
                              
                              // Datos de contacto
                              const customerName = order.customer_snapshot?.name || 'Cliente Web';
                              const customerPhone = order.customer_phone || order.customer_snapshot?.phone;
                              const customerAddress = order.shipping_address?.address || order.customer_snapshot?.address;
                              const wsNumber = formatForWhatsapp(customerPhone);
                              const requestedDate = extractRequestedOrderDate(order);
                              const requestedDateLabel = formatRequestedOrderDate(requestedDate);

                              return (
                                  <tr 
                                    key={order.id} 
                                    onClick={(e) => handleRowClick(e, order)} 
                                    className={`cursor-pointer group transition-all hover:bg-blue-50/20 ${isNew ? 'bg-blue-50/30' : ''}`}
                                  >
                                      <td className="px-4 py-3.5">
                                          <div className="font-black text-slate-800 text-[11px] uppercase flex items-center gap-2">
                                            #{order.tracking_code}
                                            {isNew && <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>}
                                          </div>
                                          <div className="text-[9px] font-bold text-slate-400 mt-0.5 flex items-center gap-1 uppercase tracking-wider">
                                            <Clock size={10}/> {formatDate(order.created_at)}
                                          </div>
                                          {requestedDate && (
                                            <div className="text-[9px] font-bold text-sky-600 mt-0.5 uppercase tracking-wider">
                                              Para: {requestedDateLabel}
                                            </div>
                                          )}
                                      </td>
                                      
                                      {/* --- COLUMNA CLIENTE MEJORADA --- */}
                                      <td className="px-3.5 py-3.5">
                                          <div className="font-bold text-slate-700 text-[10px] uppercase truncate max-w-[150px]">
                                              {customerName}
                                          </div>
                                          
                                          {/* Dirección */}
                                          {customerAddress && (
                                              <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-0.5 flex items-center gap-1 truncate max-w-[150px]" title={customerAddress}>
                                                  <MapPin size={9} /> {customerAddress}
                                              </div>
                                          )}

                                          {/* Teléfono y WhatsApp */}
                                          {customerPhone ? (
                                              <div className="flex items-center gap-2 mt-1">
                                                  <span className="text-[9px] font-bold text-slate-500 flex items-center gap-1">
                                                      <Phone size={9} /> {customerPhone}
                                                  </span>
                                                  {wsNumber && (
                                                      <a 
                                                        href={`https://wa.me/${wsNumber}`} 
                                                        target="_blank" 
                                                        rel="noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="bg-emerald-100 text-emerald-600 p-0.5 rounded hover:bg-emerald-200 transition-colors"
                                                        title="Abrir WhatsApp"
                                                      >
                                                          <MessageCircle size={10} />
                                                      </a>
                                                  )}
                                              </div>
                                          ) : (
                                              <div className="text-[9px] font-bold text-slate-300 uppercase tracking-wider mt-0.5">Sin teléfono</div>
                                          )}
                                      </td>

                                      <td className="px-3.5 py-3.5">
                                          <div className="flex items-center gap-2">
                                              <span className={`p-1.5 rounded-lg border ${order.delivery_type === 'envio' ? 'bg-purple-50 border-purple-100 text-purple-600' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                                                  {order.delivery_type === 'envio' ? <Truck size={12}/> : <Store size={12}/>}
                                              </span>
                                              <div className="text-[9px] font-black text-slate-600 uppercase tracking-widest">
                                                  {order.delivery_type}
                                              </div>
                                          </div>
                                      </td>
                                      <td className="px-3.5 py-3.5">
                                          <div className="font-black text-slate-900 text-[11px] tracking-tight">
                                              {formatCurrency(order.sales?.total)}
                                          </div>
                                          <div className="text-[8px] font-bold text-slate-400 uppercase mt-0.5 tracking-widest">
                                              {order.sales?.payment_method || 'PENDIENTE'}
                                          </div>
                                      </td>
                                      <td className="px-3.5 py-3.5 text-center">
                                          <select 
                                            value={order.status}
                                            onChange={(e) => requestStatusChange(order, e.target.value)}
                                            title="Cambia el estado operativo de este pedido."
                                            className={`outline-none text-[9px] font-black uppercase tracking-widest py-1.5 px-3 rounded-xl border cursor-pointer appearance-none text-center transition-all ${status.color}`}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                              <option value="recibido">Nuevo</option>
                                              <option value="en_preparacion">Preparando</option>
                                              {order.delivery_type === 'retiro' ? (
                                                  <>
                                                      <option value="listo">Listo p/ Retirar</option>
                                                      <option value="entregado">Retirado</option>
                                                  </>
                                              ) : (
                                                  <>
                                                      <option value="listo">Listo</option>
                                                      <option value="en_camino">En Camino</option>
                                                      <option value="entregado">Entregado</option>
                                                  </>
                                              )}
                                              <option value="cancelado">Cancelado</option>
                                          </select>
                                      </td>
                                      <td className="px-3.5 py-3.5 text-right">
                                          <div className="flex items-center justify-end gap-2">
                                              {/* BOTÓN DE COMPROBANTE DE PAGO */}
                                              {proofUrl && (
                                                  <a
                                                      href={proofUrl}
                                                      target="_blank"
                                                      rel="noreferrer"
                                                      onClick={(e) => e.stopPropagation()}
                                                      className="p-2 bg-white border border-slate-200 text-emerald-600 rounded-xl hover:text-emerald-700 hover:border-emerald-200 transition-all shadow-sm group-hover:bg-emerald-50"
                                                      title="Ver Comprobante de Pago"
                                                  >
                                                      <Paperclip size={16} />
                                                  </a>
                                              )}

                                              <button 
                                                onClick={(e) => handlePrint(e, order)}
                                                className="p-2 bg-white border border-slate-200 text-slate-500 rounded-xl hover:text-blue-600 hover:border-blue-200 transition-all shadow-sm group-hover:bg-blue-50"
                                                title={isFacturado ? "Imprimir Factura" : "Imprimir Ticket Interno"}
                                              >
                                                  <Printer size={16} />
                                              </button>
                                          </div>
                                      </td>
                                  </tr>
                              );
                          })
                      )}
                  </tbody>
              </table>
          </div>
          
          <div className="bg-slate-50 px-4 py-3 border-t border-slate-200 text-[9px] font-bold text-slate-400 uppercase tracking-widest flex flex-wrap justify-between gap-2 mt-auto">
              <span>Viendo {sortedOrders.length} pedidos</span>
              <span>Total Histórico: {orders.length}</span>
          </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 20px; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>

      {cancelConfirm.open ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0B2A36]/35 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-[28px] border border-rose-200 bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-5 py-4 border-b border-rose-100 bg-rose-50">
              <p className="text-[10px] font-black uppercase tracking-widest text-rose-500">Confirmar cancelacion</p>
              <h3 className="mt-1 text-lg font-black text-slate-800 uppercase tracking-tight">
                Cancelar pedido
              </h3>
            </div>

            <div className="px-5 py-4">
              <p className="text-[11px] font-bold text-slate-600 leading-relaxed">
                Vas a cancelar el pedido{' '}
                <span className="text-slate-800">#{cancelConfirm.order?.tracking_code || cancelConfirm.order?.id?.slice(0, 8) || ''}</span>.
              </p>
            </div>

            <div className="px-5 pb-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeCancelConfirm}
                className="px-4 py-2.5 rounded-2xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 transition-all"
              >
                No
              </button>
              <button
                type="button"
                onClick={confirmCancelOrder}
                className="px-4 py-2.5 rounded-2xl bg-rose-500 text-white text-[10px] font-black uppercase tracking-widest hover:bg-rose-600 transition-all shadow-sm"
              >
                Si, cancelar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
